import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import { Helmet } from "react-helmet";
import { motion, useViewportScroll, useTransform } from "framer-motion";
import ShimmerButton from '../components/magicui/shimmer-button';
import { CookieBanner } from '../components/CookieBanner';
import ReactGA from 'react-ga4';

ReactGA.initialize('G-78TF44GTX6');
ReactGA.send({ hitType: 'pageview', page: 'window.location.pathname' });

const Homes = () => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { scrollY, scrollYProgress } = useViewportScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const heroImgY = useTransform(scrollY, [0, 600], [0, 180]);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowCookieBanner(true);
    }
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieBanner(false);
  };

  const handleRejectCookies = () => {
    localStorage.setItem('cookieConsent', 'false');
    setShowCookieBanner(false);
  };

  const navigate = useNavigate();

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      navigate('/Form');
    }, 1000);
  };

  const handleLegalClick = () => {
    navigate('/mentions-legales');
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      num: '01',
      icon: (
        <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v18" /><path d="M8 21h8" /><path d="M2 7l10-2 10 2" />
          <circle cx="5" cy="11" r="2" /><circle cx="19" cy="11" r="2" />
        </svg>
      ),
      title: 'Forme juridique',
      description: "Identifiez la structure la plus adaptée à votre activité, votre situation personnelle et vos objectifs de développement.",
    },
    {
      num: '02',
      icon: (
        <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
        </svg>
      ),
      title: 'Budget prévisionnel',
      description: "Obtenez un budget prévisionnel détaillé avec tableau de trésorerie pour chaque forme juridique envisagée.",
    },
  ];

  const results = [
    {
      icon: (
        <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <path d="M14 2v6h6" /><path d="M12 18v-6" /><path d="m9 15 3 3 3-3" />
        </svg>
      ),
      title: 'Budget Prévisionnel',
      description: "Obtenez votre budget prévisionnel détaillé issu de la simulation réalisée",
    },
    {
      icon: (
        <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2" />
          <rect width="18" height="18" x="3" y="4" rx="2" /><circle cx="12" cy="10" r="2" />
        </svg>
      ),
      title: 'Rendez-vous Expert',
      description: "Prenez rendez-vous avec un expert en création d'entreprise pour personnaliser votre projet",
    },
  ];

  return (
    <>
      {showCookieBanner && <CookieBanner onAccept={handleAcceptCookies} onReject={handleRejectCookies} />}
      <Helmet>
        <title>FB Conseils - Création d'entreprise, forme juridique et budget prévisionnel</title>
        <meta name="description" content="FB Conseils vous accompagne dans la création de votre entreprise. Simulateur de forme juridique gratuit, budget prévisionnel personnalisé et conseils d'experts." />
        <link rel="canonical" href="https://maformejuridique.fr/" />
        <meta property="og:title" content="FB Conseils - Création d'entreprise et forme juridique" />
        <meta property="og:description" content="Simulateur de forme juridique gratuit, budget prévisionnel personnalisé et conseils d'experts pour créer votre entreprise." />
        <meta property="og:url" content="https://maformejuridique.fr/" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      {loading ? (
        <div className="flex justify-center items-center w-full h-screen">
          <Loader />
        </div>
      ) : (
        <>
          {isMobile && (
            <motion.div className="progress-bar" style={{ scaleX, originX: 0, scaleY: 2, backgroundColor: '#45B8A9' }} />
          )}

          <div className="overflow-hidden" id="home">

            {/* ==================== HERO ==================== */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
              <motion.div className="absolute inset-0" style={{ y: heroImgY }}>
                <img
                  src="/img/montagne.jpeg"
                  alt="Paysage montagneux symbolisant l'accompagnement en création d'entreprise par FB Conseils"
                  className="w-full h-[120%] object-cover"
                  draggable="false"
                />
              </motion.div>
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(170deg, rgba(0,57,66,0.2) 0%, rgba(0,57,66,0.5) 40%, rgba(0,42,48,0.88) 100%)',
                }}
              />

              <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-16 pb-24">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="mb-8"
                >
                  <span
                    className="inline-block text-xs md:text-sm tracking-[0.3em] uppercase font-medium px-5 py-2.5 rounded-full border"
                    style={{
                      color: '#45B8A9',
                      borderColor: 'rgba(69,184,169,0.35)',
                      backgroundColor: 'rgba(69,184,169,0.08)',
                      fontFamily: "'Outfit', sans-serif",
                    }}
                  >
                    Conseil en création d'entreprise
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.4 }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.08] mb-8"
                  style={{ fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                >
                  Créez votre entreprise
                  <br />
                  <span style={{ color: '#45B8A9' }}>en toute confiance</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-14"
                  style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                >
                  Grâce à notre expertise, nous vous accompagnons dans le choix de votre
                  forme juridique et la réalisation de votre budget prévisionnel
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="flex justify-center"
                >
                  <ShimmerButton
                    onClick={handleClick}
                    background="#45B8A9"
                    shimmerColor="#ffffff"
                    size="large"
                  >
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                      Commencer la simulation
                    </span>
                  </ShimmerButton>
                </motion.div>
              </div>

              {/* Scroll indicator */}
              <motion.div
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                <span
                  className="text-xs tracking-[0.2em] uppercase"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: "'Outfit', sans-serif" }}
                >
                  Défiler
                </span>
                <motion.svg
                  width="16" height="24" viewBox="0 0 16 24" fill="none"
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                >
                  <rect x="1" y="1" width="14" height="22" rx="7" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                  <motion.rect
                    x="6" y="5" width="4" height="4" rx="2" fill="rgba(255,255,255,0.45)"
                    animate={{ y: [0, 9, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                  />
                </motion.svg>
              </motion.div>
            </section>

            {/* ==================== SERVICES ==================== */}
            <section className="relative py-24 lg:py-32" style={{ background: 'linear-gradient(180deg, #f8fffe 0%, #eef7f5 100%)' }}>
              <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.04] pointer-events-none select-none">
                <img src="/img/montagne2.jpeg" alt="Illustration montagne FB Conseils" className="w-[500px]" draggable="false" />
              </div>

              <div className="max-w-6xl mx-auto px-6 relative z-10">
                <motion.div
                  className="text-center mb-20"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7 }}
                >
                  <span
                    className="text-sm tracking-[0.25em] uppercase font-medium"
                    style={{ color: '#45B8A9', fontFamily: "'Outfit', sans-serif" }}
                  >
                    Notre accompagnement
                  </span>
                  <h2
                    className="text-3xl md:text-4xl lg:text-5xl mt-4"
                    style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                  >
                    Une expertise à votre service
                  </h2>
                  <div className="w-16 h-0.5 mx-auto mt-6" style={{ backgroundColor: '#45B8A9' }} />
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8 lg:gap-10 max-w-4xl mx-auto">
                  {services.map((service, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.6, delay: index * 0.15 }}
                      className="group relative bg-white rounded-2xl p-8 lg:p-10 hover:shadow-xl transition-all duration-500 border border-gray-100/80 overflow-hidden"
                      style={{ boxShadow: '0 1px 3px rgba(0,57,66,0.04)' }}
                    >
                      <span
                        className="absolute -top-3 -right-1 text-8xl font-bold pointer-events-none select-none"
                        style={{ color: 'rgba(69,184,169,0.06)', fontFamily: "'Cormorant', serif" }}
                      >
                        {service.num}
                      </span>
                      <div
                        className="absolute bottom-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                        style={{ backgroundColor: '#45B8A9' }}
                      />
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                        style={{ backgroundColor: 'rgba(69,184,169,0.1)' }}
                      >
                        {service.icon}
                      </div>
                      <h3
                        className="text-2xl mb-3"
                        style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                      >
                        {service.title}
                      </h3>
                      <p className="text-gray-500 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {service.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* ==================== SIMULATOR CTA ==================== */}
            <section className="relative py-24 lg:py-32 overflow-hidden" style={{ backgroundColor: '#003942' }}>
              <div className="absolute inset-0 opacity-[0.03]">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                  <pattern id="topo" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                    <path d="M100 0Q150 50 100 100Q50 150 100 200" fill="none" stroke="white" strokeWidth="0.5" />
                    <path d="M0 100Q50 50 100 100Q150 150 200 100" fill="none" stroke="white" strokeWidth="0.5" />
                    <circle cx="50" cy="50" r="30" fill="none" stroke="white" strokeWidth="0.3" />
                    <circle cx="150" cy="150" r="45" fill="none" stroke="white" strokeWidth="0.3" />
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#topo)" />
                </svg>
              </div>

              <div className="relative z-10 grid lg:grid-cols-2 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className="px-6 lg:pl-[max(2rem,calc((100vw-72rem)/2+1.5rem))] lg:pr-12 lg:max-w-[50vw]"
                >
                  <span
                    className="text-sm tracking-[0.25em] uppercase font-medium"
                    style={{ color: '#45B8A9', fontFamily: "'Outfit', sans-serif" }}
                  >
                    Notre simulateur
                  </span>
                  <h2
                    className="text-3xl md:text-4xl lg:text-5xl text-white leading-tight mt-4 mb-6"
                    style={{ fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                  >
                    Trouvez la forme juridique{' '}
                    <span style={{ color: '#45B8A9' }}>la plus adaptée</span>{' '}
                    à votre projet
                  </h2>
                  <p
                    className="text-lg mb-3"
                    style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                  >
                    À votre situation personnelle et à vos besoins
                  </p>
                  <p
                    className="text-2xl md:text-3xl mb-10"
                    style={{ color: '#45B8A9', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                  >
                    grâce à notre SIMULATEUR !
                  </p>
                  <ShimmerButton
                    onClick={handleClick}
                    background="#45B8A9"
                    shimmerColor="#ffffff"
                  >
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                      Lancer la simulation
                    </span>
                  </ShimmerButton>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="relative hidden md:flex items-center justify-end"
                >
                  <img
                    src="/img/montagne3-removebg-preview.png"
                    alt="Simulateur FB Conseils"
                    className="w-[55%] ml-auto drop-shadow-2xl"
                    draggable="false"
                  />
                </motion.div>
              </div>
            </section>

            {/* ==================== RESULTS ==================== */}
            <section className="py-24 lg:py-32 pb-40" style={{ background: 'linear-gradient(180deg, #eef7f5 0%, #f8fffe 100%)' }}>
              <div className="max-w-6xl mx-auto px-6">
                <motion.div
                  className="text-center mb-20"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                >
                  <span
                    className="text-sm tracking-[0.25em] uppercase font-medium"
                    style={{ color: '#45B8A9', fontFamily: "'Outfit', sans-serif" }}
                  >
                    Vos résultats
                  </span>
                  <h2
                    className="text-3xl md:text-4xl lg:text-5xl mt-4"
                    style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                  >
                    À l'issue de la simulation
                  </h2>
                  <p
                    className="text-gray-500 text-lg mt-4 max-w-2xl mx-auto"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    Accédez à des solutions concrètes pour lancer votre projet entrepreneurial
                  </p>
                  <div className="w-16 h-0.5 mx-auto mt-6" style={{ backgroundColor: '#45B8A9' }} />
                </motion.div>

                <div className="grid md:grid-cols-2 gap-10 lg:gap-14 max-w-4xl mx-auto">
                  {results.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.15 }}
                      className="text-center group"
                    >
                      <div
                        className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: 'rgba(69,184,169,0.1)' }}
                      >
                        {item.icon}
                      </div>
                      <h3
                        className="mb-3"
                        style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700, fontSize: '1.4rem' }}
                      >
                        {item.title}
                      </h3>
                      <p className="text-gray-500 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {item.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* ==================== FOOTER ==================== */}
            <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-gray-100 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row justify-between items-center py-4 space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500" style={{ fontFamily: "'Outfit', sans-serif" }}>© 2024</span>
                    <span className="text-sm font-medium text-gray-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      FBCONSEILS - SIRET 931 237 192
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleLegalClick}
                      className="text-sm text-gray-500 hover:text-[#003942] transition-colors duration-200 px-3 py-2 rounded-md hover:bg-gray-50"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Mentions Légales
                    </button>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        </>
      )}
    </>
  );
};

export default Homes;
